/**
 * AiCloud Layout - Cloudflare dashboard gibi sol sidebar
 * AiCloud altindaki tum sayfalar bu layout'u kullanir
 */

import {
    Bot,
    ChevronDown,
    ChevronRight,
    Cloud,
    Code2,
    Database,
    FolderOpen,
    Globe,
    HardDrive,
    HelpCircle,
    Home,
    Key,
    Layers,
    MessageSquare,
    Monitor,
    Network,
    Plus,
    Server,
    Settings,
    Terminal,
    Zap
} from 'lucide-react'
import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import Topbar from './Topbar'

interface NavGroup {
  label: string
  icon: React.ElementType
  basePath: string
  children: { label: string; path: string; icon: React.ElementType }[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Cloud Providers',
    icon: Cloud,
    basePath: '/aicloud/app/platforms',
    children: [
      { label: 'Cloudflare', path: '/aicloud/app/platforms/cloudflare', icon: Cloud },
      { label: 'Azure', path: '/aicloud/app/platforms/azure', icon: Server },
      { label: 'AWS', path: '/aicloud/app/platforms/aws', icon: HardDrive },
      { label: 'Google Cloud', path: '/aicloud/app/platforms/gcp', icon: Database },
      { label: 'Huawei Cloud', path: '/aicloud/app/platforms/huawei', icon: Cloud },
      { label: 'Digital Ocean', path: '/aicloud/app/platforms/digitalocean', icon: Globe },
      { label: 'IBM Cloud', path: '/aicloud/app/platforms/ibm', icon: Layers },
      { label: 'Alibaba Cloud', path: '/aicloud/app/platforms/alibaba', icon: Globe },
    ]
  },
  {
    label: 'Agent Platforms',
    icon: Layers,
    basePath: '/aicloud/app/platforms/api',
    children: [
      { label: 'Cloud API Keys', path: '/aicloud/app/platforms/api-keys', icon: Key },
      { label: 'Local LLM', path: '/aicloud/app/platforms/local-llm', icon: Monitor },
      { label: 'GPU Providers', path: '/aicloud/app/platforms/gpu-providers', icon: Server },
      { label: 'Petals P2P', path: '/aicloud/app/platforms/petals', icon: Network },
      { label: 'WebLLM', path: '/aicloud/app/platforms/webllm', icon: Globe },
    ]
  },
  {
    label: 'Agents',
    icon: Bot,
    basePath: '/aicloud/app/agents',
    children: [
      { label: 'My Agents', path: '/aicloud/app/agents', icon: Bot },
      { label: 'Add Agent', path: '/aicloud/app/agents/add', icon: Plus },
    ]
  },
  {
    label: 'Skills',
    icon: Zap,
    basePath: '/aicloud/app/skills',
    children: [
      { label: 'Marketplace', path: '/aicloud/app/skills', icon: Zap },
      { label: 'Installed', path: '/aicloud/app/skills/installed', icon: Code2 },
    ]
  },
  {
    label: 'Projects',
    icon: FolderOpen,
    basePath: '/aicloud/app/projects',
    children: [
      { label: 'Work', path: '/aicloud/app/projects/work', icon: FolderOpen },
      { label: 'Research', path: '/aicloud/app/projects/research', icon: FolderOpen },
    ]
  },
  {
    label: 'Social',
    icon: MessageSquare,
    basePath: '/aicloud/app/social',
    children: [
      { label: 'Feed', path: '/aicloud/app/social', icon: MessageSquare },
    ]
  },
  {
    label: 'RunAI Chat',
    icon: MessageSquare,
    basePath: '/aicloud/app/chat',
    children: [
      { label: 'Chat with Agents', path: '/aicloud/app/chat', icon: MessageSquare },
    ]
  },
]

export default function AiCloudLayout() {
  const location = useLocation()

  // Hangi grup acik - path'e gore otomatik ac
  const getInitialOpen = () => {
    const open: Record<string, boolean> = {}
    NAV_GROUPS.forEach(g => {
      const groupActive = g.children.some(c => location.pathname.startsWith(c.path))
        || location.pathname.startsWith(g.basePath)
      if (groupActive) {
        open[g.label] = true
      }
    })
    // Hicbiri yoksa Cloud Providers acik gelsin
    if (Object.keys(open).length === 0) {open['Cloud Providers'] = true}
    return open
  }

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(getInitialOpen)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('aicloud-sidebar-collapsed') === 'true'
  })

  const toggleGroup = (label: string) => {
    if (sidebarCollapsed) {setSidebarCollapsed(false)}
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }))
  }

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed
    setSidebarCollapsed(newState)
    localStorage.setItem('aicloud-sidebar-collapsed', newState ? 'true' : 'false')
  }

  const isActive = (path: string) => location.pathname === path

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Topbar />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sol Sidebar */}
        <aside style={{
          width: sidebarCollapsed ? '64px' : '220px',
          minWidth: sidebarCollapsed ? '64px' : '220px',
          background: 'var(--card-bg)',
          borderRight: '1px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          padding: '16px 0',
          transition: 'width 0.2s, min-width 0.2s',
          overflowX: 'hidden'
        }}>
          {/* Baslik */}
          <div style={{
            padding: sidebarCollapsed ? '0 8px 16px' : '0 16px 16px',
            borderBottom: '1px solid var(--border-color)',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'space-between'
          }}>
          <Link to="/aicloud" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              color: 'var(--text-primary)',
              fontWeight: 700,
              fontSize: '15px'
            }}>
              <Cloud size={18} color="#3b82f6" style={{ flexShrink: 0 }} />
              {!sidebarCollapsed && <span>AiCloud</span>}
            </Link>
            {!sidebarCollapsed && (
              <button 
                onClick={toggleSidebar} 
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                <ChevronDown size={16} style={{ transform: 'rotate(90deg)' }} />
              </button>
            )}
          </div>
          
          {sidebarCollapsed && (
             <button 
                onClick={toggleSidebar} 
                style={{ margin: '0 auto 8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                <ChevronRight size={16} />
              </button>
          )}

          {/* Dashboard linki */}
          <Link
            to="/aicloud/app/dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: sidebarCollapsed ? '8px' : '8px 16px',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              textDecoration: 'none',
              fontSize: '13px',
              color: isActive('/aicloud/app/dashboard') ? '#3b82f6' : 'var(--text-secondary)',
              background: isActive('/aicloud/app/dashboard') ? 'rgba(59,130,246,0.08)' : 'transparent',
              borderLeft: isActive('/aicloud/app/dashboard') ? '2px solid #3b82f6' : '2px solid transparent',
              fontWeight: isActive('/aicloud/app/dashboard') ? 600 : 400,
              marginBottom: '4px',
            }}
            title="Dashboard"
          >
            <Home size={14} style={{ flexShrink: 0 }} />
            {!sidebarCollapsed && <span>Dashboard</span>}
          </Link>

          {/* RunAI OS linki */}
          <Link
            to="/dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: sidebarCollapsed ? '8px' : '8px 16px',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              textDecoration: 'none',
              fontSize: '13px',
              color: isActive('/dashboard') ? '#6366f1' : 'var(--text-secondary)',
              background: isActive('/dashboard') ? 'rgba(99,102,241,0.08)' : 'transparent',
              borderLeft: isActive('/dashboard') ? '2px solid #6366f1' : '2px solid transparent',
              fontWeight: isActive('/dashboard') ? 600 : 400,
              marginBottom: '4px',
            }}
            title="RunAI OS"
          >
            <Monitor size={14} style={{ flexShrink: 0 }} />
            {!sidebarCollapsed && <span>RunAI OS</span>}
          </Link>


          {/* Nav Gruplari */}
          {NAV_GROUPS.map(group => {
            const GroupIcon = group.icon
            const isOpen = openGroups[group.label] ?? false
            const groupActive = group.children.some(c => location.pathname.startsWith(c.path))
              || location.pathname.startsWith(group.basePath)

            return (
              <div key={group.label}>
                {/* Grup baslik */}
                <button
                  onClick={() => toggleGroup(group.label)}
                  title={group.label}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'space-between',
                    gap: '8px',
                    padding: sidebarCollapsed ? '8px' : '8px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: groupActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontSize: '13px',
                    fontWeight: groupActive ? 600 : 500,
                    textAlign: 'left'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', width: sidebarCollapsed ? '100%' : 'auto' }}>
                    <GroupIcon size={15} style={{ flexShrink: 0 }} />
                    {!sidebarCollapsed && <span>{group.label}</span>}
                  </div>
                  {!sidebarCollapsed && (
                    isOpen
                      ? <ChevronDown size={13} />
                      : <ChevronRight size={13} />
                  )}
                </button>

                {/* Alt linkler */}
                {isOpen && !sidebarCollapsed && group.children.map(item => {
                  const ItemIcon = item.icon
                  const active = isActive(item.path)
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '7px 16px 7px 36px',
                        textDecoration: 'none',
                        fontSize: '13px',
                        color: active ? '#3b82f6' : 'var(--text-secondary)',
                        background: active ? 'rgba(59,130,246,0.08)' : 'transparent',
                        borderLeft: active ? '2px solid #3b82f6' : '2px solid transparent',
                        fontWeight: active ? 600 : 400,
                        transition: 'all 0.15s'
                      }}
                    >
                      <ItemIcon size={13} style={{ flexShrink: 0 }} />
                      {item.label}
                    </Link>
                  )
                })}
              </div>
            )
          })}

          {/* Alt - Settings */}
          <div style={{ marginTop: 'auto', borderTop: '1px solid var(--border-color)', padding: '12px 0 0' }}>
            <Link
              to="/aicloud/app/settings"
              title="Settings"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: sidebarCollapsed ? '8px' : '8px 16px',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                textDecoration: 'none',
                fontSize: '13px',
                color: isActive('/aicloud/app/settings') ? '#3b82f6' : 'var(--text-secondary)',
              }}
            >
              <Settings size={14} style={{ flexShrink: 0 }} />
              {!sidebarCollapsed && <span>Settings</span>}
            </Link>
            <Link
              to="/aicloud/app/cli"
              title="CLI Download"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: sidebarCollapsed ? '8px' : '8px 16px',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                textDecoration: 'none',
                fontSize: '13px',
                color: isActive('/aicloud/app/cli') ? '#3b82f6' : 'var(--text-secondary)',
              }}
            >
              <Terminal size={14} style={{ flexShrink: 0 }} />
              {!sidebarCollapsed && <span>CLI Download</span>}
            </Link>
            <Link
              to="/aicloud/app/faq"
              title="Help & FAQ"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: sidebarCollapsed ? '8px' : '8px 16px',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                textDecoration: 'none',
                fontSize: '13px',
                color: isActive('/aicloud/app/faq') ? '#3b82f6' : 'var(--text-secondary)',
              }}
            >
              <HelpCircle size={14} style={{ flexShrink: 0 }} />
              {!sidebarCollapsed && <span>Help & FAQ</span>}
            </Link>
          </div>
        </aside>

        {/* Icerik alani */}
        <main style={{
          flex: 1,
          overflowY: 'auto',
          background: 'var(--body-bg)',
          padding: '24px'
        }}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
